import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import { getMarketVolatility, getAnalyzerResults, getAnalyzerResult, getSPY } from '../api';

// ── Theme ──────────────────────────────────────────────────────────────────
const C = {
  pageBg:  '#F1F5F9',
  cardBg:  '#FFFFFF',
  border:  '#E2E8F0',
  text:    '#0F172A',
  textMd:  '#475569',
  textSm:  '#94A3B8',
  primary: '#2563EB',
  success: '#16A34A',
  danger:  '#DC2626',
  warning: '#F59E0B',
  neutral: '#94A3B8',
  purple:  '#7C3AED',
  shadow:  '0 1px 3px rgba(0,0,0,0.08)',
};

const S = {
  page:      { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text, padding: '24px 28px' },
  wrap:      { maxWidth: 1300, margin: '0 auto' },
  card:      { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, marginBottom: 20 },
  cardHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${C.border}` },
  cardTitle: { fontWeight: 600, fontSize: 14, color: C.text },
  filterCard: {
    background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12,
    boxShadow: C.shadow, padding: '16px 20px', marginBottom: 16,
    display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap',
  },
  statsRow:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 },
  statCard:  { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', boxShadow: C.shadow },
  statLabel: { fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 },
  statValue: { fontSize: 20, fontWeight: 700, color: C.text },
  statSub:   { fontSize: 12, color: C.textMd, marginTop: 2 },
  label:     { display: 'block', fontSize: 12, fontWeight: 500, color: C.textMd, marginBottom: 4 },
  select:    { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, background: C.cardBg, cursor: 'pointer', outline: 'none' },
  input:     { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, background: C.cardBg, width: 120, outline: 'none' },
  inputSm:   { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, background: C.cardBg, width: 75, outline: 'none' },
  periodBtn: (active) => ({
    padding: '5px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 500,
    background: active ? C.primary : 'transparent',
    color: active ? '#fff' : C.textMd,
    border: `1px solid ${active ? C.primary : C.border}`,
  }),
};

// ── Regime config ──────────────────────────────────────────────────────────
const REGIME = {
  bull:     { color: '#22C55E', opacity: 0.08, darkColor: C.success, emoji: '🟢', label: 'Bull Market' },
  bear:     { color: '#EF4444', opacity: 0.08, darkColor: C.danger,  emoji: '🔴', label: 'Bear Market' },
  sideways: { color: '#EAB308', opacity: 0.05, darkColor: C.warning, emoji: '🟡', label: 'Sideways' },
};

// ── Known market events ────────────────────────────────────────────────────
const KNOWN_EVENTS = {
  '2025-04-09': 'Trump tariff war',
  '2025-04-07': 'Market panic',
  '2025-04-04': 'Trade war fears',
  '2025-04-03': 'Tariff announcement',
  '2025-01-27': 'DeepSeek shock',
  '2025-02-21': 'AI selloff',
  '2025-08-05': 'Yen carry unwind',
  '2024-08-05': 'Yen carry trade',
  '2024-03-08': 'Tech selloff',
  '2025-11-07': 'Election reaction',
};

// ── Loading steps ──────────────────────────────────────────────────────────
const STEPS = [
  'Завантаження даних волатильності...',
  'Завантаження SPY...',
  'Розрахунок A/D Line...',
  'Розрахунок Cumulative Return...',
  'Визначення ринкових режимів...',
  'Готово ✅',
];

// ── Helpers ────────────────────────────────────────────────────────────────
function daysBetween(a, b) {
  const ms = new Date(b + 'T12:00:00Z') - new Date(a + 'T12:00:00Z');
  return Math.max(0, Math.round(ms / 86400000));
}

function durationLabel(n) {
  if (n === 1) return '1 день';
  if (n < 5)  return `${n} дні`;
  return `${n} днів`;
}

function snapRegimesToDates(regimes, sortedDates) {
  if (!regimes.length || !sortedDates.length) return [];
  const last = sortedDates[sortedDates.length - 1];
  const snap = (d) => {
    if (!d || d === 'ongoing') return last;
    for (const cd of sortedDates) { if (cd >= d) return cd; }
    return last;
  };
  return regimes.map(r => ({ ...r, x1: snap(r.start), x2: snap(r.end) }));
}

function aggregateData(data, period) {
  if (period === 'day') return data;
  const groups = new Map();
  for (const d of data) {
    let key;
    const date = new Date(d.date + 'T12:00:00Z');
    if (period === 'week') {
      const day  = date.getUTCDay();
      const diff = day === 0 ? -6 : 1 - day;
      const mon  = new Date(date);
      mon.setUTCDate(date.getUTCDate() + diff);
      key = mon.toISOString().slice(0, 10);
    } else {
      key = d.date.slice(0, 7);
    }
    if (!groups.has(key)) {
      groups.set(key, { date: key, days: 0, up_count: 0, down_count: 0, flat_count: 0, total_w: 0, sum_am_w: 0, sum_vi_w: 0, max_up: 0, max_down: 0 });
    }
    const g = groups.get(key);
    const w = d.total || 1;
    g.days++; g.up_count += d.up_count; g.down_count += d.down_count; g.flat_count += d.flat_count;
    g.total_w += w; g.sum_am_w += d.avg_move * w; g.sum_vi_w += d.volatility_index * w;
    g.max_up   = Math.max(g.max_up,  d.max_up ?? 0);
    g.max_down = Math.min(g.max_down, d.max_down ?? 0);
  }
  return Array.from(groups.values()).map(g => ({
    date: g.date, up_count: g.up_count, down_count: g.down_count, flat_count: g.flat_count,
    total: g.total_w,
    avg_move:         g.total_w > 0 ? +(g.sum_am_w / g.total_w).toFixed(2) : 0,
    volatility_index: g.total_w > 0 ? +(g.sum_vi_w / g.total_w).toFixed(2) : 0,
    max_up: g.max_up, max_down: g.max_down, days: g.days,
  }));
}

// ── Sub-components ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div style={S.statCard}>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statValue, color: color || C.text }}>{value}</div>
      {sub && <div style={S.statSub}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, badge, children }) {
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <span style={S.cardTitle}>{title}</span>
        {badge && <span style={{ fontSize: 11, color: C.textSm }}>{badge}</span>}
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

const EventLabel = ({ viewBox, value, color }) => {
  const { x, y } = viewBox;
  return (
    <text x={x + 3} y={y + 4} fill={color || C.danger} fontSize={9} fontWeight={600}
      transform={`rotate(-55, ${x + 3}, ${y + 4})`} style={{ userSelect: 'none' }}>
      {value}
    </text>
  );
};

function ProgressCard({ currentStep, onCancel }) {
  const progress = Math.round((currentStep / (STEPS.length - 1)) * 100);
  return (
    <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: C.shadow }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 16 }}>⏳ Аналіз ринку...</div>
      <div style={{ marginBottom: 16 }}>
        {STEPS.map((step, i) => {
          const done    = i < currentStep;
          const active  = i === currentStep;
          const pending = i > currentStep;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, fontSize: 13 }}>
              <span style={{ width: 18, textAlign: 'center', flexShrink: 0,
                color: done ? C.success : active ? C.warning : C.textSm }}>
                {done ? '✅' : active ? '⏳' : '○'}
              </span>
              <span style={{ color: done ? C.success : active ? C.text : C.textSm, fontWeight: active ? 600 : 400 }}>
                {step}
              </span>
            </div>
          );
        })}
      </div>
      {/* Progress bar */}
      <div style={{ background: C.border, borderRadius: 4, height: 8, marginBottom: 8, overflow: 'hidden' }}>
        <div style={{ background: C.primary, height: '100%', borderRadius: 4, width: `${progress}%`, transition: 'width 0.25s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <span style={{ fontSize: 12, color: C.textMd }}>{progress}%</span>
        <button
          onClick={onCancel}
          style={{ padding: '6px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ⏹ Cancel
        </button>
      </div>
    </div>
  );
}

function RegimesTable({ regimes }) {
  if (!regimes.length) return null;
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <span style={S.cardTitle}>Ринкові режими</span>
        <span style={{ fontSize: 11, color: C.textSm }}>{regimes.length} {regimes.length === 1 ? 'період' : 'періодів'}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Режим', 'Початок', 'Кінець', 'Тривалість'].map(h => (
                <th key={h} style={{ textAlign: h === 'Тривалість' ? 'right' : 'left', padding: '10px 16px', fontSize: 11, color: C.textSm, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {regimes.map((r, i) => {
              const cfg = REGIME[r.type] || REGIME.sideways;
              const ongoing = r.end === 'ongoing';
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: ongoing ? '#F8FAFC' : 'transparent' }}>
                  <td style={{ padding: '9px 16px', color: cfg.darkColor, fontWeight: ongoing ? 700 : 500 }}>
                    {cfg.emoji} {cfg.label}
                  </td>
                  <td style={{ padding: '9px 16px', color: C.text, fontWeight: ongoing ? 700 : 400 }}>{r.start}</td>
                  <td style={{ padding: '9px 16px', color: ongoing ? C.primary : C.textMd, fontWeight: ongoing ? 700 : 400 }}>
                    {ongoing ? 'ongoing →' : r.end}
                  </td>
                  <td style={{ padding: '9px 16px', textAlign: 'right', color: C.textMd, fontWeight: ongoing ? 700 : 400 }}>
                    {durationLabel(r.duration)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tooltips ───────────────────────────────────────────────────────────────
const tipBox = {
  background: '#fff', border: '1px solid #E2E8F0',
  borderRadius: 8, padding: '8px 12px', fontSize: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const up   = payload.find(p => p.dataKey === 'up_count');
  const flat = payload.find(p => p.dataKey === 'flat_count');
  const down = payload.find(p => p.dataKey === 'down_count');
  const tot  = (up?.value ?? 0) + (flat?.value ?? 0) + (down?.value ?? 0);
  const pct  = v => tot ? Math.round(v / tot * 100) : 0;
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ color: C.success }}>▲ Up:   <b>{up?.value ?? 0}</b> ({pct(up?.value ?? 0)}%)</div>
      <div style={{ color: C.neutral }}>– Flat: <b>{flat?.value ?? 0}</b> ({pct(flat?.value ?? 0)}%)</div>
      <div style={{ color: C.danger }}>▼ Down: <b>{down?.value ?? 0}</b> ({pct(down?.value ?? 0)}%)</div>
    </div>
  );
};

const makeAvgMoveTip = (overlayName) => ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const am = payload.find(p => p.dataKey === 'avg_move');
  const eq = payload.find(p => p.dataKey === 'eq_pct');
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      {am && <div style={{ color: C.warning, fontWeight: 600 }}>Avg move: {am.value?.toFixed(2)}%</div>}
      {eq && eq.value != null && (
        <div style={{ color: C.purple, fontWeight: 600 }}>
          {overlayName}: {eq.value >= 0 ? '+' : ''}{eq.value.toFixed(2)}%
        </div>
      )}
    </div>
  );
};

const VolIdxTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      <div style={{ fontWeight: 700, color: C.primary }}>Vol Index: {payload[0]?.value?.toFixed(2)}</div>
    </div>
  );
};

const SpyTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const close = payload.find(p => p.dataKey === 'close');
  const ma50  = payload.find(p => p.dataKey === 'ma50');
  const above = close && ma50?.value && close.value >= ma50.value;
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      {close && <div style={{ fontWeight: 700, color: C.primary }}>SPY: ${close.value?.toFixed(2)}</div>}
      {ma50?.value && (
        <div style={{ color: C.warning }}>
          MA50: ${ma50.value?.toFixed(2)}
          <span style={{ marginLeft: 6, color: above ? C.success : C.danger }}>({above ? '▲ above' : '▼ below'})</span>
        </div>
      )}
    </div>
  );
};

const BreadthTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const adl = payload.find(p => p.dataKey === 'ad_line');
  const adv = payload.find(p => p.dataKey === 'advance_decline');
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      {adv && <div style={{ color: adv.value >= 0 ? C.success : C.danger }}>Daily A/D: {adv.value >= 0 ? '+' : ''}{adv.value?.toFixed(1)}%</div>}
      {adl && <div style={{ fontWeight: 700, color: C.primary }}>AD Line: {adl.value?.toFixed(1)}</div>}
    </div>
  );
};

const CumulTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const cum = payload.find(p => p.dataKey === 'cumulative');
  const ma  = payload.find(p => p.dataKey === 'ma20');
  return (
    <div style={tipBox}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      {cum && (
        <div style={{ fontWeight: 700, color: cum.value >= 100 ? C.success : C.danger }}>
          Cumulative: {cum.value?.toFixed(2)} ({cum.value >= 100 ? '+' : ''}{(cum.value - 100).toFixed(2)}%)
        </div>
      )}
      {ma?.value && <div style={{ color: C.warning }}>MA20: {ma.value?.toFixed(2)}</div>}
    </div>
  );
};

// ── RegimeBg: ReferenceAreas for all charts ────────────────────────────────
function RegimeBg({ regimes, yAxisId }) {
  return regimes.map((r, i) => {
    const cfg = REGIME[r.type] || REGIME.sideways;
    return (
      <ReferenceArea
        key={i} x1={r.x1} x2={r.x2}
        fill={cfg.color} fillOpacity={cfg.opacity}
        stroke="none"
        {...(yAxisId ? { yAxisId } : {})}
      />
    );
  });
}

// ── Main component ─────────────────────────────────────────────────────────
export default function MarketVolatility() {
  // ── fetch-related filters ────────────────────────────────────────────────
  const [dataType,  setDataType]  = useState('daily');
  const [fromDate,  setFromDate]  = useState('2025-10-01');
  const [toDate,    setToDate]    = useState('2026-02-28');
  const [threshold, setThreshold] = useState(1.0);

  // ── display filters (don't trigger refetch) ──────────────────────────────
  const [aggPeriod, setAggPeriod] = useState('day');

  // ── data ─────────────────────────────────────────────────────────────────
  const [data,    setData]    = useState([]);
  const [spyData, setSpyData] = useState([]);

  // ── status machine ───────────────────────────────────────────────────────
  const [status,      setStatus]      = useState('idle');   // idle | loading | done
  const [loadingStep, setLoadingStep] = useState(0);
  const [error,       setError]       = useState(null);
  const abortRef = useRef(null);

  // ── overlay strategy ─────────────────────────────────────────────────────
  const [resultsList,    setResultsList]    = useState([]);
  const [overlayId,      setOverlayId]      = useState('');
  const [overlayEquity,  setOverlayEquity]  = useState([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlayName,    setOverlayName]    = useState('');

  // ── actions ───────────────────────────────────────────────────────────────
  const handleRun = async () => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setLoadingStep(0);
    setData([]);
    setSpyData([]);
    setError(null);

    try {
      // Step 0 → Step 1: market volatility
      const volRes = await getMarketVolatility(
        { type: dataType, from: fromDate, to: toDate, threshold },
        { signal: ctrl.signal },
      );
      setData(volRes.data);

      // Step 1 → Step 2: SPY (non-fatal if fails)
      setLoadingStep(1);
      try {
        const spyRes = await getSPY({ from: fromDate, to: toDate }, { signal: ctrl.signal });
        setSpyData(spyRes.data);
      } catch (e) {
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') throw e;
        setSpyData([]);
      }

      // Steps 2-4: client-side computations (instant, just show progress)
      setLoadingStep(2);
      await new Promise(r => setTimeout(r, 40));
      setLoadingStep(3);
      await new Promise(r => setTimeout(r, 40));
      setLoadingStep(4);
      await new Promise(r => setTimeout(r, 40));
      setLoadingStep(5);

      setStatus('done');
    } catch (err) {
      const cancelled = err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError';
      if (cancelled) {
        setData([]);
        setSpyData([]);
      } else {
        setError(err?.response?.data?.detail || err.message || 'Error loading data');
      }
      setStatus('idle');
      setLoadingStep(0);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  // ── load results list (always, for overlay dropdown) ─────────────────────
  useEffect(() => {
    getAnalyzerResults({ limit: 500 })
      .then(res => setResultsList((res.data.items || []).filter(r => r.status === 'done')))
      .catch(() => {});
  }, []);

  // ── load equity curve when overlay selection changes ─────────────────────
  useEffect(() => {
    if (!overlayId) { setOverlayEquity([]); setOverlayName(''); return; }
    setOverlayLoading(true);
    getAnalyzerResult(parseInt(overlayId, 10))
      .then(res => { setOverlayEquity(res.data.equity_curve || []); setOverlayName(res.data.name || `Strategy #${overlayId}`); })
      .catch(() => { setOverlayEquity([]); setOverlayName(''); })
      .finally(() => setOverlayLoading(false));
  }, [overlayId]);

  // ── memos ─────────────────────────────────────────────────────────────────

  // Basic stats from raw data
  const stats = useMemo(() => {
    if (!data.length) return null;
    const sorted = [...data].sort((a, b) => b.avg_move - a.avg_move);
    return {
      mostVolatile:  sorted[0],
      mostCalm:      sorted[sorted.length - 1],
      totalAvgMove:  data.reduce((s, d) => s + d.avg_move, 0) / data.length,
      avgVolIdx:     data.reduce((s, d) => s + d.volatility_index, 0) / data.length,
    };
  }, [data]);

  // Aggregated data for charts 1-3
  const aggData = useMemo(() => aggregateData(data, aggPeriod), [data, aggPeriod]);

  // Equity overlay sorted
  const equityPctSorted = useMemo(() => {
    if (!overlayEquity.length) return [];
    const base = overlayEquity[0].balance;
    return overlayEquity
      .map(e => ({ date: e.date.slice(0, 10), pct: +((e.balance / base - 1) * 100).toFixed(3) }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [overlayEquity]);

  // chartData = aggData + equity overlay merged
  const chartData = useMemo(() => aggData.map(d => {
    let eqPct = null;
    if (equityPctSorted.length) {
      for (const e of equityPctSorted) {
        if (e.date <= d.date) eqPct = e.pct; else break;
      }
    }
    return { ...d, eq_pct: eqPct };
  }), [aggData, equityPctSorted]);

  // Event markers (top-5 volatile days, daily mode only)
  const eventMarkers = useMemo(() => {
    if (!data.length || aggPeriod !== 'day') return [];
    return [...data].sort((a, b) => b.avg_move - a.avg_move).slice(0, 5)
      .map(d => ({ date: d.date, label: KNOWN_EVENTS[d.date] || `${d.avg_move.toFixed(1)}% move`, avgMove: d.avg_move }));
  }, [data, aggPeriod]);

  // ── Daily merged data: cumulative + AD line + per-day regime ──────────────
  const dailyMergedData = useMemo(() => {
    if (!data.length) return [];

    // Step 1: cumulative median (starts at 100)
    let cum = 100;
    const arr = data.map(d => {
      cum = +(cum * (1 + (d.median_move || 0) / 100)).toFixed(5);
      return { ...d, cumulative: cum };
    });

    // Step 2: MA20 of cumulative
    arr.forEach((d, i) => {
      d.ma20 = i >= 19
        ? +(arr.slice(i - 19, i + 1).reduce((s, x) => s + x.cumulative, 0) / 20).toFixed(5)
        : null;
    });

    // Step 3: cumulative AD line
    let adLine = 0;
    arr.forEach(d => {
      const total = d.total || 1;
      const ad = (d.up_count - d.down_count) / total * 100;
      adLine = +(adLine + ad).toFixed(3);
      d.ad_line = adLine;
      d.advance_decline = +ad.toFixed(2);
    });

    // Step 4: regime per day (compare to 5 days ago for AD trend)
    arr.forEach((d, i) => {
      const adPrev    = arr[Math.max(0, i - 5)].ad_line;
      const aboveMA   = d.ma20 != null ? d.cumulative > d.ma20 : d.cumulative >= 100;
      const adRising  = d.ad_line > adPrev;
      d.regime = aboveMA && adRising ? 'bull' : !aboveMA && !adRising ? 'bear' : 'sideways';
    });

    return arr;
  }, [data]);

  // ── Market regimes: list of transitions ───────────────────────────────────
  const marketRegimes = useMemo(() => {
    if (!dailyMergedData.length) return [];
    const regimes = [];
    let currentType = null;
    let regimeStart = null;

    for (const d of dailyMergedData) {
      if (d.regime !== currentType) {
        if (currentType !== null) {
          regimes.push({ type: currentType, start: regimeStart, end: d.date, duration: daysBetween(regimeStart, d.date) });
        }
        currentType = d.regime;
        regimeStart = d.date;
      }
    }
    // Ongoing regime
    if (currentType !== null) {
      const today = new Date().toISOString().slice(0, 10);
      regimes.push({ type: currentType, start: regimeStart, end: 'ongoing', duration: daysBetween(regimeStart, today), ongoing: true });
    }
    return regimes;
  }, [dailyMergedData]);

  // ── Current regime details card data ─────────────────────────────────────
  const currentRegimeDetails = useMemo(() => {
    if (!marketRegimes.length || !dailyMergedData.length) return null;
    const cur  = marketRegimes[marketRegimes.length - 1];
    const last = dailyMergedData[dailyMergedData.length - 1];
    const n    = dailyMergedData.length;

    const startDayIdx = dailyMergedData.findIndex(d => d.date >= cur.start);
    const cumStart    = startDayIdx >= 0 ? dailyMergedData[startDayIdx].cumulative : null;
    const medReturn   = cumStart ? +((last.cumulative / cumStart - 1) * 100).toFixed(1) : null;

    let spyReturn = null;
    if (spyData.length) {
      const spyStart = spyData.find(d => d.date >= cur.start);
      const spyLast  = spyData[spyData.length - 1];
      if (spyStart && spyLast && spyStart.close > 0)
        spyReturn = +((spyLast.close / spyStart.close - 1) * 100).toFixed(1);
    }

    const adTrend = n >= 6 ? last.ad_line - dailyMergedData[n - 6].ad_line : 0;
    return { regime: cur, spyReturn, medReturn, adTrend };
  }, [marketRegimes, dailyMergedData, spyData]);

  // ── Snapped regime backgrounds for each chart ─────────────────────────────
  const chartRegimes   = useMemo(() => snapRegimesToDates(marketRegimes, chartData.map(d => d.date)), [marketRegimes, chartData]);
  const spyRegimes     = useMemo(() => snapRegimesToDates(marketRegimes, spyData.map(d => d.date)), [marketRegimes, spyData]);
  const dailyRegimes   = useMemo(() => snapRegimesToDates(marketRegimes, dailyMergedData.map(d => d.date)), [marketRegimes, dailyMergedData]);

  // ── Period summary (bull/bear/sideways count + days) ──────────────────────
  const periodSummary = useMemo(() => {
    if (!marketRegimes.length) return null;
    const s = { bull: { count: 0, days: 0 }, bear: { count: 0, days: 0 }, sideways: { count: 0, days: 0 }, total_days: 0 };
    marketRegimes.forEach(r => {
      if (s[r.type]) { s[r.type].count++; s[r.type].days += r.duration; }
      s.total_days += r.duration;
    });
    return s;
  }, [marketRegimes]);

  // ── Daily data with linear trend (least squares on cumulative) ────────────
  const { dailyWithTrendData, trendSlope } = useMemo(() => {
    if (!dailyMergedData.length) return { dailyWithTrendData: dailyMergedData, trendSlope: 0 };
    const n    = dailyMergedData.length;
    const yArr = dailyMergedData.map(d => d.cumulative);
    const sumX  = (n * (n - 1)) / 2;
    const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
    const sumY  = yArr.reduce((a, b) => a + b, 0);
    const sumXY = yArr.reduce((s, y, i) => s + i * y, 0);
    const slope     = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const data = dailyMergedData.map((d, i) => ({ ...d, trend: +(intercept + slope * i).toFixed(5) }));
    return { dailyWithTrendData: data, trendSlope: slope };
  }, [dailyMergedData]);

  // ── Misc ──────────────────────────────────────────────────────────────────
  const xTickFmt = v => v ? (aggPeriod === 'month' ? v.slice(0, 7) : v.slice(5)) : '';
  const hasOverlay = overlayId && overlayEquity.length > 0;
  const AvgMoveTip = useMemo(() => makeAvgMoveTip(overlayName), [overlayName]);
  const isLoading  = status === 'loading';
  const isDone     = status === 'done';

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Market Volatility</div>
          <div style={{ fontSize: 13, color: C.textMd, marginTop: 4 }}>
            Щоденний аналіз руху ринку — кількість акцій що виросли/впали, SPY, A/D Line, ринкові режими
          </div>
        </div>

        {/* ── Filters (disabled during loading) ─────────────────────────── */}
        <div style={S.filterCard}>
          <div>
            <label style={S.label}>Data type</label>
            <select style={{ ...S.select, opacity: isLoading ? 0.5 : 1 }} value={dataType} onChange={e => setDataType(e.target.value)} disabled={isLoading}>
              <option value="daily">Daily</option>
              <option value="hourly">Hourly</option>
            </select>
          </div>
          <div>
            <label style={S.label}>From</label>
            <input type="date" style={{ ...S.input, opacity: isLoading ? 0.5 : 1 }} value={fromDate} onChange={e => setFromDate(e.target.value)} disabled={isLoading} />
          </div>
          <div>
            <label style={S.label}>To</label>
            <input type="date" style={{ ...S.input, opacity: isLoading ? 0.5 : 1 }} value={toDate} onChange={e => setToDate(e.target.value)} disabled={isLoading} />
          </div>
          <div>
            <label style={S.label}>Threshold (%)</label>
            <input type="number" style={{ ...S.inputSm, opacity: isLoading ? 0.5 : 1 }} value={threshold} step="0.1" min="0.1"
              onChange={e => setThreshold(parseFloat(e.target.value) || 0.5)} disabled={isLoading} />
          </div>

          {/* Aggregation (display only, always enabled) */}
          {isDone && (
            <div>
              <label style={S.label}>Aggregation</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {['day', 'week', 'month'].map(p => (
                  <button key={p} style={S.periodBtn(aggPeriod === p)} onClick={() => setAggPeriod(p)}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Overlay strategy (always enabled) */}
          {isDone && (
            <div>
              <label style={S.label}>Overlay strategy{overlayLoading ? ' ⏳' : ''}</label>
              <select
                style={{ ...S.select, minWidth: 200, maxWidth: 280, color: overlayId ? C.purple : C.textMd }}
                value={overlayId} onChange={e => setOverlayId(e.target.value)}>
                <option value="">None</option>
                {resultsList.map(r => (
                  <option key={r.id} value={r.id}>
                    #{r.id} {r.name ? r.name.slice(0, 35) : '—'} ({(r.created_at || '').slice(0, 10)})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ── Action buttons ────────────────────────────────────────────── */}
        {status === 'idle' && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={handleRun}
              style={{ padding: '9px 24px', background: C.success, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              ▶ Run Analysis
            </button>
          </div>
        )}

        {status === 'loading' && <ProgressCard currentStep={loadingStep} onCancel={handleCancel} />}

        {status === 'done' && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={handleRun}
              style={{ padding: '9px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              🔄 Recalculate
            </button>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {error && (
          <div style={{ background: '#FEE2E2', color: C.danger, padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* ── Idle placeholder ──────────────────────────────────────────── */}
        {status === 'idle' && !error && (
          <div style={{
            textAlign: 'center', padding: '64px 0',
            background: C.cardBg, border: `2px dashed ${C.border}`,
            borderRadius: 16, color: C.textSm, fontSize: 15,
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
            <div style={{ fontWeight: 600, color: C.textMd, marginBottom: 6 }}>
              Оберіть параметри та натисніть ▶ Run Analysis
            </div>
            <div style={{ fontSize: 13 }}>
              SPY · Advance-Decline Line · Cumulative Return · Ринкові режими
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            RESULTS (shown only when status === "done")
        ══════════════════════════════════════════════════════════════════ */}
        {isDone && (
          <>
            {/* ── Current Regime Card ───────────────────────────────────── */}
            {currentRegimeDetails && (() => {
              const { regime, spyReturn, medReturn, adTrend } = currentRegimeDetails;
              const cfg = REGIME[regime.type] || REGIME.sideways;
              return (
                <div style={{ ...S.card, borderLeft: `4px solid ${cfg.darkColor}`, marginBottom: 20 }}>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: cfg.darkColor, marginBottom: 6 }}>
                          {cfg.emoji} {cfg.label}
                        </div>
                        <div style={{ fontSize: 13, color: C.textMd }}>
                          Почався: <b style={{ color: C.text }}>{regime.start}</b>
                          <span style={{ marginLeft: 8, color: C.textSm }}>({durationLabel(regime.duration)} тому)</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                        {spyReturn !== null && (
                          <div style={{ textAlign: 'center', minWidth: 90 }}>
                            <div style={{ fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>SPY від початку</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: spyReturn >= 0 ? C.success : C.danger }}>
                              {spyReturn >= 0 ? '+' : ''}{spyReturn}%
                            </div>
                          </div>
                        )}
                        {medReturn !== null && (
                          <div style={{ textAlign: 'center', minWidth: 100 }}>
                            <div style={{ fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Медіана акцій</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: medReturn >= 0 ? C.success : C.danger }}>
                              {medReturn >= 0 ? '+' : ''}{medReturn}%
                            </div>
                          </div>
                        )}
                        <div style={{ textAlign: 'center', minWidth: 90 }}>
                          <div style={{ fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>AD Line тренд</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: adTrend >= 0 ? C.success : C.danger }}>
                            {adTrend >= 0 ? '↗ зростає' : '↘ падає'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Overlay info bar */}
            {hasOverlay && (
              <div style={{ marginBottom: 16, padding: '8px 16px', background: '#EDE9FE', border: '1px solid #C4B5FD', borderRadius: 8, fontSize: 12, color: C.purple, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="4"><line x1="0" y1="2" x2="16" y2="2" stroke={C.purple} strokeWidth="2" strokeDasharray="4 2"/></svg>
                Overlay: <b>{overlayName}</b> — equity % return від старту (права вісь на графіку Avg Move)
              </div>
            )}

            {/* ── Stat cards ────────────────────────────────────────────── */}
            {stats && (
              <div style={S.statsRow}>
                <StatCard label="Most Volatile Day"   value={stats.mostVolatile.date} sub={`avg move: ${stats.mostVolatile.avg_move.toFixed(2)}%`}  color={C.danger} />
                <StatCard label="Most Calm Day"       value={stats.mostCalm.date}     sub={`avg move: ${stats.mostCalm.avg_move.toFixed(2)}%`}        color={C.success} />
                <StatCard label="Avg Move / Day"      value={`${stats.totalAvgMove.toFixed(2)}%`} sub={`${data.length} trading days`} />
                <StatCard label="Avg Volatility Index" value={stats.avgVolIdx.toFixed(1)} sub="avg_move × active_ratio × 100" />
              </div>
            )}

            {/* ── Chart 1: Stacked bar ──────────────────────────────────── */}
            {chartData.length > 0 && (
              <ChartCard
                title={`Market Movement (threshold ±${threshold}%)`}
                badge={`${data.length} days · ${chartData.length} ${aggPeriod}s`}>
                <div style={{ marginBottom: 8, fontSize: 12, color: C.textMd }}>
                  Зелений / сірий / червоний = виросли / флет / впали. Фон = ринковий режим.
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 16, right: 8, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false} tickFormatter={xTickFmt} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: C.textSm }} tickLine={false} />
                    <Tooltip content={<BarTip />} />
                    <Legend iconType="rect" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                    <RegimeBg regimes={chartRegimes} />
                    <Bar dataKey="up_count"   stackId="a" fill={C.success} name={`Up ≥${threshold}%`}   radius={[0,0,0,0]} />
                    <Bar dataKey="flat_count" stackId="a" fill="#CBD5E1"   name="Flat"                   radius={[0,0,0,0]} />
                    <Bar dataKey="down_count" stackId="a" fill={C.danger}  name={`Down ≥${threshold}%`} radius={[2,2,0,0]} />
                    {eventMarkers.map(m => (
                      <ReferenceLine key={m.date} x={m.date} stroke="#1E40AF" strokeDasharray="3 3" strokeWidth={1.5}
                        label={<EventLabel value={m.label.slice(0, 18)} color="#1E40AF" />} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* ── Chart 2: Avg Move + Equity Overlay ───────────────────── */}
            {chartData.length > 0 && (
              <ChartCard
                title="Average Move (%) per Day"
                badge={hasOverlay ? `+ ${overlayName} equity overlay` : 'mean |daily_return| across all stocks'}>
                {hasOverlay && (
                  <div style={{ marginBottom: 8, display: 'flex', gap: 16, fontSize: 12 }}>
                    <span style={{ color: C.warning }}>━━ Avg market move (left)</span>
                    <span style={{ color: C.purple }}>╌╌ {overlayName} equity % (right)</span>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={hasOverlay ? 260 : 220}>
                  <LineChart data={chartData} margin={{ top: 16, right: hasOverlay ? 56 : 8, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false} tickFormatter={xTickFmt} interval="preserveStartEnd" />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: C.textSm }} tickLine={false} tickFormatter={v => v + '%'} />
                    {hasOverlay && (
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: C.purple }} tickLine={false}
                        tickFormatter={v => (v >= 0 ? '+' : '') + v.toFixed(0) + '%'} width={52} />
                    )}
                    <Tooltip content={<AvgMoveTip />} />
                    <RegimeBg regimes={chartRegimes} yAxisId="left" />
                    {stats && (
                      <ReferenceLine yAxisId="left" y={stats.totalAvgMove} stroke={C.textSm} strokeDasharray="4 4"
                        label={{ value: `avg ${stats.totalAvgMove.toFixed(2)}%`, position: 'insideTopRight', fontSize: 10, fill: C.textSm }} />
                    )}
                    {hasOverlay && <ReferenceLine yAxisId="right" y={0} stroke={C.purple} strokeDasharray="2 4" strokeOpacity={0.4} />}
                    {eventMarkers.map(m => (
                      <ReferenceLine key={m.date} x={m.date} yAxisId="left" stroke={C.danger} strokeDasharray="3 3" strokeWidth={1.5}
                        label={<EventLabel value={m.label.slice(0, 18)} />} />
                    ))}
                    <Line yAxisId="left" type="monotone" dataKey="avg_move" stroke={C.warning} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Avg Move" />
                    {hasOverlay && (
                      <Line yAxisId="right" type="monotone" dataKey="eq_pct" stroke={C.purple} strokeWidth={2} strokeDasharray="5 3"
                        dot={false} connectNulls activeDot={{ r: 4 }} name={overlayName} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* ── Chart 3: Volatility Index ─────────────────────────────── */}
            {chartData.length > 0 && (
              <ChartCard title="Volatility Index" badge="avg_move × (up+down)/total × 100">
                <div style={{ marginBottom: 8, fontSize: 12, color: C.textMd }}>
                  Дні вище середнього позначені червоними точками. Фон = ринковий режим.
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 16, right: 8, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false} tickFormatter={xTickFmt} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: C.textSm }} tickLine={false} />
                    <Tooltip content={<VolIdxTip />} />
                    <RegimeBg regimes={chartRegimes} />
                    {stats && (
                      <ReferenceLine y={stats.avgVolIdx} stroke={C.danger} strokeDasharray="4 4"
                        label={{ value: `avg ${stats.avgVolIdx.toFixed(1)}`, position: 'insideTopRight', fontSize: 10, fill: C.danger }} />
                    )}
                    {eventMarkers.map(m => (
                      <ReferenceLine key={m.date} x={m.date} stroke={C.danger} strokeDasharray="3 3" strokeWidth={1.5}
                        label={<EventLabel value={m.label.slice(0, 18)} />} />
                    ))}
                    <Line type="monotone" dataKey="volatility_index" stroke={C.primary} strokeWidth={2}
                      dot={props => {
                        const { cx, cy, payload } = props;
                        return payload.volatility_index > (stats?.avgVolIdx ?? 0)
                          ? <circle key={cx} cx={cx} cy={cy} r={3} fill={C.danger} stroke="none" />
                          : null;
                      }}
                      activeDot={{ r: 4 }} name="Volatility Index" />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* ── Chart 4: SPY + MA50 ───────────────────────────────────── */}
            {spyData.length > 0 && (
              <ChartCard
                title="S&P 500 — SPY ETF"
                badge={`${spyData.length} trading days · фон = ринковий режим (A/D + cumulative median)`}>
                <div style={{ marginBottom: 8, fontSize: 12, color: C.textMd }}>
                  Синя лінія = SPY. Жовтий пунктир = MA50. Зелений/червоний/жовтий фон = Bull/Bear/Sideways режим.
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={spyData} margin={{ top: 16, right: 8, left: 10, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false}
                      tickFormatter={v => v ? v.slice(5) : ''} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: C.textSm }} tickLine={false} tickFormatter={v => '$' + v} domain={['auto', 'auto']} width={58} />
                    <Tooltip content={<SpyTip />} />
                    <RegimeBg regimes={spyRegimes} />
                    <Line type="monotone" dataKey="ma50" stroke={C.warning} strokeWidth={1.5} strokeDasharray="5 3" dot={false} connectNulls name="MA50" />
                    <Line type="monotone" dataKey="close" stroke={C.primary} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="SPY" />
                    <Legend iconType="line" iconSize={14} wrapperStyle={{ fontSize: 12 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* ── Chart 5: Advance-Decline Line ─────────────────────────── */}
            {dailyMergedData.length > 0 && (
              <ChartCard
                title="Market Breadth — Advance-Decline Line"
                badge="(up − down) / total × 100, накопичувально · щоденні дані">
                <div style={{ marginBottom: 8, fontSize: 12, color: C.textMd }}>
                  <span style={{ color: C.success, fontWeight: 600 }}>AD Line росте</span> = широкий бичачий ринок ✅ &nbsp;·&nbsp;
                  <span style={{ color: C.danger, fontWeight: 600 }}>AD Line падає</span> = ведмежий ринок ❌ &nbsp;·&nbsp;
                  флет при рості SPY = слабкий ринок ⚠️
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dailyMergedData} margin={{ top: 16, right: 52, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false}
                      tickFormatter={v => v ? v.slice(5) : ''} interval="preserveStartEnd" />
                    <YAxis yAxisId="cumul" tick={{ fontSize: 11, fill: C.primary }} tickLine={false} />
                    <YAxis yAxisId="daily" orientation="right" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false} tickFormatter={v => v + '%'} width={46} />
                    <Tooltip content={<BreadthTip />} />
                    <RegimeBg regimes={dailyRegimes} yAxisId="cumul" />
                    <ReferenceLine yAxisId="cumul" y={0} stroke={C.textSm} strokeDasharray="4 4" strokeOpacity={0.5} />
                    <Line yAxisId="daily" type="monotone" dataKey="advance_decline" stroke={C.neutral} strokeWidth={1} strokeOpacity={0.45} dot={false} name="Daily A/D %" />
                    <Line yAxisId="cumul" type="monotone" dataKey="ad_line" stroke={C.primary} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="AD Line (cumul)" />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* ── Chart 6: Cumulative Median Return ─────────────────────── */}
            {dailyMergedData.length > 0 && (() => {
              const trendColor = trendSlope > 0.05 ? C.success : trendSlope < -0.05 ? C.danger : C.neutral;
              return (
                <ChartCard
                  title="Cumulative Median Stock Return"
                  badge="старт = 100 · медіана % по всіх акціях · не залежить від мега-кап · щоденні дані">
                  <div style={{ marginBottom: 8, fontSize: 12, color: C.textMd, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span>Якщо лінія вище 100 — середня акція в плюсі. Не спотворена NVDA/AAPL.</span>
                    <span style={{ color: C.warning }}>━━ MA20</span>
                    <span style={{ color: trendColor }}>╌╌ Тренд ({trendSlope > 0 ? '+' : ''}{trendSlope.toFixed(4)}/день)</span>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={dailyWithTrendData} margin={{ top: 16, right: 8, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textSm }} tickLine={false}
                        tickFormatter={v => v ? v.slice(5) : ''} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: C.textSm }} tickLine={false} domain={['auto', 'auto']} />
                      <Tooltip content={<CumulTip />} />
                      <RegimeBg regimes={dailyRegimes} />
                      <ReferenceLine y={100} stroke={C.textSm} strokeDasharray="4 4" strokeOpacity={0.6}
                        label={{ value: '100', position: 'insideTopLeft', fontSize: 10, fill: C.textSm }} />
                      <Line type="monotone" dataKey="trend" stroke={trendColor} strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls name="Тренд" />
                      <Line type="monotone" dataKey="ma20" stroke={C.warning} strokeWidth={1.5} strokeDasharray="5 3" dot={false} connectNulls name="MA20" />
                      <Line type="monotone" dataKey="cumulative" stroke={C.success} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Cumulative Median" />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              );
            })()}

            {/* ── Period Analysis ───────────────────────────────────────── */}
            {periodSummary && (() => {
              const bullPct  = periodSummary.bull.days  / periodSummary.total_days;
              const bearPct  = periodSummary.bear.days  / periodSummary.total_days;
              const spyChange = spyData.length >= 2
                ? (spyData[spyData.length - 1].close / spyData[0].close - 1) * 100
                : null;
              const dom    = ['bull', 'bear', 'sideways'].reduce((a, b) => periodSummary[a].days >= periodSummary[b].days ? a : b);
              const domCfg = REGIME[dom];
              const domPct = (periodSummary[dom].days / periodSummary.total_days * 100).toFixed(0);
              let conclusion;
              if (bullPct > 0.55 && (spyChange === null || spyChange > 5))
                conclusion = `🟢 Переважно бичачий ринок — ${(bullPct * 100).toFixed(0)}% часу ринок ріс${spyChange !== null ? `, SPY +${spyChange.toFixed(1)}% за період` : ''}`;
              else if (bearPct > 0.4 || (spyChange !== null && spyChange < -5))
                conclusion = `🔴 Переважно ведмежий ринок — ${(bearPct * 100).toFixed(0)}% часу ринок падав${spyChange !== null ? `, SPY ${spyChange.toFixed(1)}% за період` : ''}`;
              else
                conclusion = `🟡 Змішаний ринок без чіткого тренду — Bull ${(bullPct * 100).toFixed(0)}% / Bear ${(bearPct * 100).toFixed(0)}%${spyChange !== null ? `, SPY ${spyChange >= 0 ? '+' : ''}${spyChange.toFixed(1)}% за період` : ''}`;

              return (
                <div style={S.card}>
                  <div style={S.cardHead}>
                    <span style={S.cardTitle}>Загальний аналіз періоду</span>
                    <span style={{ fontSize: 11, color: C.textSm }}>{periodSummary.total_days} торгових днів · {marketRegimes.length} режимів</span>
                  </div>
                  <div style={{ padding: '16px 20px' }}>

                    {/* Conclusion */}
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 14, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8, border: `1px solid ${C.border}` }}>
                      {conclusion}
                    </div>

                    {/* Mini Timeline */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: C.textSm, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Таймлайн режимів
                      </div>
                      <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', cursor: 'default' }}>
                        {marketRegimes.map(r => (
                          <div
                            key={r.start}
                            title={`${REGIME[r.type]?.label || r.type}: ${r.start} → ${r.end} (${r.duration}д)`}
                            style={{
                              flex: r.duration,
                              background: r.type === 'bull' ? '#22c55e' : r.type === 'bear' ? '#ef4444' : '#eab308',
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.textSm, marginTop: 3 }}>
                        <span>{marketRegimes[0]?.start?.slice(0, 7)}</span>
                        <span>{marketRegimes[Math.floor(marketRegimes.length / 2)]?.start?.slice(0, 7)}</span>
                        <span>{marketRegimes[marketRegimes.length - 1]?.start?.slice(0, 7)}</span>
                      </div>
                    </div>

                    {/* 3 cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                      {['bull', 'bear', 'sideways'].map(type => {
                        const cfg  = REGIME[type];
                        const s    = periodSummary[type];
                        const pct  = periodSummary.total_days > 0 ? (s.days / periodSummary.total_days * 100).toFixed(0) : 0;
                        const ep   = s.count;
                        return (
                          <div key={type} style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', borderTop: `3px solid ${cfg.darkColor}` }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: cfg.darkColor, marginBottom: 4 }}>{cfg.emoji} {cfg.label}</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.1 }}>
                              {s.days} <span style={{ fontSize: 13, fontWeight: 400, color: C.textMd }}>днів</span>
                            </div>
                            <div style={{ fontSize: 13, color: C.textMd, margin: '2px 0' }}>{pct}% часу</div>
                            <div style={{ fontSize: 12, color: C.textSm }}>
                              {ep} {ep === 1 ? 'епізод' : ep < 5 ? 'епізоди' : 'епізодів'}
                              {ep > 0 && <> · avg {(s.days / ep).toFixed(1)}д</>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer: dominant + spy + avg durations */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: C.textMd }}>
                      <span>Домінуючий: <b style={{ color: domCfg.darkColor }}>{domCfg.emoji} {domCfg.label} ({domPct}%)</b></span>
                      {spyChange !== null && (
                        <span>SPY за весь період: <b style={{ color: spyChange >= 0 ? C.success : C.danger }}>{spyChange >= 0 ? '+' : ''}{spyChange.toFixed(1)}%</b></span>
                      )}
                      {periodSummary.bull.count > 0 && (
                        <span>Avg Bull: <b>{(periodSummary.bull.days / periodSummary.bull.count).toFixed(1)}д</b></span>
                      )}
                      {periodSummary.bear.count > 0 && (
                        <span>Avg Bear: <b>{(periodSummary.bear.days / periodSummary.bear.count).toFixed(1)}д</b></span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Regimes table ─────────────────────────────────────────── */}
            <RegimesTable regimes={marketRegimes} />

            {/* ── Event markers legend ──────────────────────────────────── */}
            {eventMarkers.length > 0 && (
              <div style={S.card}>
                <div style={S.cardHead}>
                  <span style={S.cardTitle}>Top Volatile Days</span>
                  <span style={{ fontSize: 11, color: C.textSm }}>auto-detected from data</span>
                </div>
                <div style={{ padding: '12px 20px', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {eventMarkers.map((m, i) => (
                    <div key={m.date} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 7, padding: '6px 12px', fontSize: 12 }}>
                      <span style={{ color: C.danger, fontWeight: 700 }}>#{i + 1}</span>
                      <span style={{ fontWeight: 600, color: C.text }}>{m.date}</span>
                      <span style={{ color: C.textMd }}>{m.label}</span>
                      <span style={{ color: C.danger, fontWeight: 600 }}>avg {m.avgMove.toFixed(2)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
